import React, { MouseEvent } from 'react';
import { ArrowDown, ArrowUp } from 'phosphor-react';

import { Button } from '../Button';
import { ListItem } from '../ListItem';
import { ListPopover } from '../ListPopover';

import { HeaderColumn, TableProps } from './types';
import { TableHeaderCellWrapper } from './Table.styles';

type TableHeaderCellProps<D extends object> = {
  header: HeaderColumn<D>;
  columns: TableProps<D>['columns'];
  sort?: TableProps<D>['sort'];
  sortable?: boolean;
  onSortChange?: TableProps<D>['onSortChange'];
  index: number;
};

const SORT_ICONS = {
  asc: <ArrowUp weight="bold" size={14} />,
  desc: <ArrowDown weight="bold" size={14} />,
};

function TableHeaderCell<D extends object>({
  header,
  columns,
  sort,
  sortable,
  onSortChange,
  index,
  ...props
}: TableHeaderCellProps<D>): JSX.Element | null {
  const { key, ...restHeaderProps } = header.getHeaderProps();
  void key;

  const column = columns[index];
  const accessor = column?.accessor as string;
  const activeSortDirection =
    !sort || !accessor
      ? null
      : column.customSortKey
      ? sort[column.customSortKey]
      : sort?.[accessor];
  const sortIcon = activeSortDirection ? SORT_ICONS[activeSortDirection] : null;

  const handleSortChange = (
    direction: 'asc' | 'desc',
    event: React.MouseEvent<HTMLTableCellElement> | undefined,
  ) => {
    if (!onSortChange) return;
    const sort = { [accessor]: direction };
    if (column.customSortKey) {
      onSortChange({ [column.customSortKey]: direction }, event);
    } else {
      onSortChange(sort, event);
    }
  };

  return (
    <TableHeaderCellWrapper {...props} {...restHeaderProps} active={!!activeSortDirection}>
      <div>
        {!!sortable && !!header.sortable ? (
          <ListPopover
            appendTo={document.body}
            opener={({ isOpen }) => (
              <Button
                size="small"
                variant="secondary"
                active={!!activeSortDirection}
                selected={isOpen}
                endAdornment={sortIcon}
                disabled={!onSortChange}
                {...restHeaderProps}
              >
                {header.render('Header')}
              </Button>
            )}
          >
            <ListItem
              size="small"
              primaryText="Sort ascending"
              avatar={SORT_ICONS.asc}
              active={activeSortDirection === 'asc'}
              onClick={(e: MouseEvent<HTMLTableCellElement>) => handleSortChange('asc', e)}
            />
            <ListItem
              size="small"
              primaryText="Sort descending"
              avatar={SORT_ICONS.desc}
              active={activeSortDirection === 'desc'}
              onClick={(e: MouseEvent<HTMLTableCellElement>) => handleSortChange('desc', e)}
            />
          </ListPopover>
        ) : (
          header.render('Header')
        )}
      </div>
    </TableHeaderCellWrapper>
  );
}

export default TableHeaderCell;
